import type { Lote } from '@abastece/core';
import { hoy } from '@abastece/shared';
import type { Clave } from '../i18n/index.js';

export type EstadoVencimiento = 'vencido' | 'critico' | 'proximo' | 'ok';

export interface LoteConEstado extends Lote {
  dias: number;
  estado: EstadoVencimiento;
}

const DIA = 86_400_000;

/**
 * Whole days from `dia` (today at the shop) to `fecha`; negative once it has passed.
 *
 * "Today" used to be the UTC date, so from six in the evening every lot was a day closer to
 * expiring than it was, and one due tomorrow already showed as expiring today.
 */
export function diasHasta(fecha: string, dia = hoy()): number {
  return Math.round((Date.parse(`${fecha}T00:00:00Z`) - Date.parse(`${dia}T00:00:00Z`)) / DIA);
}

/**
 * Three bands, not two.
 *
 * "Already expired" and "expires next week" need different actions — one is a write-off that
 * should already have happened, the other is a reason to discount and move it. Collapsing them
 * into a single warning is how expired stock stays on the shelf.
 */
export function clasificar(dias: number, umbralDias: number): EstadoVencimiento {
  if (dias < 0) return 'vencido';
  if (dias <= Math.min(7, umbralDias)) return 'critico';
  if (dias <= umbralDias) return 'proximo';
  return 'ok';
}

export function tablero(lotes: readonly Lote[], umbralDias: number, dia = hoy()): LoteConEstado[] {
  return lotes
    .filter((l) => l.cantidadDisponible > 0)
    .map((l) => {
      const dias = diasHasta(l.vencimiento, dia);
      return { ...l, dias, estado: clasificar(dias, umbralDias) };
    })
    .filter((l) => l.estado !== 'ok')
    .sort((a, b) => a.dias - b.dias);
}

/**
 * Products with units past their date at a location, each with its oldest such date. A sale
 * draws down the oldest batch first, so ringing one up sells the expired units in the books,
 * whatever the customer took off the shelf.
 */
export function vencidosEn(lotes: readonly Lote[], ubicacion: string, dia = hoy()): Map<string, string> {
  const vencidos = new Map<string, string>();
  for (const l of lotes) {
    if (l.ubicacion !== ubicacion || l.cantidadDisponible <= 0 || diasHasta(l.vencimiento, dia) >= 0) continue;
    const antes = vencidos.get(l.productoId);
    if (!antes || l.vencimiento < antes) vencidos.set(l.productoId, l.vencimiento);
  }
  return vencidos;
}

export const ETIQUETA: Record<EstadoVencimiento, Clave> = {
  vencido: 'venc.estado.vencido',
  critico: 'venc.estado.critico',
  proximo: 'venc.estado.proximo',
  ok: 'venc.estado.ok',
};

export const COLOR: Record<EstadoVencimiento, string> = {
  vencido: 'bg-rose-500/15 text-rose-300',
  critico: 'bg-amber-500/15 text-amber-300',
  proximo: 'bg-slate-700/60 text-slate-300',
  ok: 'bg-slate-800 text-slate-400',
};
